import { DocumentNode } from '@apollo/client';
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverTrigger,
  Tooltip,
  useClipboard,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useContext, useEffect, useState, useMemo } from 'react';
import {
  Detection,
  AlertEntity,
  PaginatedAlertEntities,
  ServiceNameEnum,
} from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import { IQueryAlertMetadataExcludedEntitiesPaginated } from '../../../queries';
import { DetectionQueryVariables } from '../../../pages';
import { IGetDetectionVariables } from '../../DetectionDrawer/Panels/SummaryPanel/SummaryPanel';
import { AddNoteContent } from '../../DetectionNotes';
import { SuppressEntityMenuItem } from '@investigator/shared/components/SuppressEntityMenuItem';
import { DetectionNotesDrawer } from '../../DetectionDrawer';
import { Modal } from '@gamma/ui/overlay';

import AccountCircleOutlinedIcon from '@gamma/ui/icons/AccountCircleOutlinedIcon';
import CheckCircleOutlinedIcon from '@gamma/ui/icons/CheckCircleOutlinedIcon';
import CommentSharpIcon from '@gamma/ui/icons/CommentSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import ForwardSharpIcon from '@gamma/ui/icons/ForwardSharpIcon';
import MoreVertSharpIcon from '@gamma/ui/icons/MoreVertSharpIcon';
import { SlideDrawerContext } from '@investigator/shared/providers';
import { useQueryEscalationConfigurationsPublic } from '../../../../integrations/queries';
import {
  AssignUserContent,
  CloseDetectionContent,
  SendToContent,
  SuppressEntityContent,
} from '../PopoverContent';
import { useDetectionContext } from '@investigator/features/detections/context';
import {
  getSourceEntityForSuppression,
  getEntityInstanceFromDetection,
} from '@investigator/shared/utils';
import type { SantizedDetection } from '../../../pages/Detections';
import { useEntitiesContext } from '@investigator/features/entities/context/EntitiesContext';
import { useEntitySuppressionStatus } from '@investigator/features/entities/hooks';
const {
  sendTo,
  copiedUrl,
  detectionIsSent,
  copyDetectionUrl,
  SNConfigureAndEnable,
  close,
  controls: { assignTo },
  detection: { notes, addNote },
} = i18n.pages.detections;
const { excludeEntity } = i18n.pages.entityDetails;
const { detectionIsClosed, suppress, unSuppress } = excludeEntity;

interface ActionsMenuProps {
  detection?: Detection | SantizedDetection;
  isRelated?: boolean;
  isRowActions?: boolean;
  refetchQueries: (
    | { query: DocumentNode; variables: IGetDetectionVariables }
    | { query: DocumentNode; variables: DetectionQueryVariables }
  )[];
  excludedEntitiesData?: IQueryAlertMetadataExcludedEntitiesPaginated;
  excludedEntitiesLoading: boolean;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  userRole?: string;
  metadataLoading?: boolean;
}

// Convert GraphQL AlertEntity to local AlertEntity
function convertAlertEntity(entity: AlertEntity): AlertEntity {
  return {
    entity_id: entity.entity_id ?? '',
    entity_name: entity.entity_name ?? '',
    entity_type: entity.entity_type ?? '',
    entity_category: entity.entity_category ?? null, // Use Maybe<EntityCategory>
  };
}

// Utility to sanitize Detection from GraphQL to local type
function sanitizePaginatedEntity(
  entity: PaginatedAlertEntities | null | undefined,
): PaginatedAlertEntities {
  return {
    offset: entity?.offset ?? 0,
    size: entity?.size ?? 0,
    alert_entities:
      entity?.alert_entities
        ?.filter((e): e is NonNullable<typeof e> => e != null)
        .map(convertAlertEntity) ?? [],
    total_items: entity?.total_items ?? 0,
    sort:
      entity?.sort?.filter((s): s is NonNullable<typeof s> => s != null) ?? [],
  };
}

function sanitizeDetection(
  detection: Detection | undefined,
): SantizedDetection | undefined {
  if (!detection) return undefined;
  const keys = detection.detection_keys ?? {
    tenant_detection: '',
    tenant_detection_entity: '',
    tenant_entity: '',
  };
  const alertInfo = detection.alert_info ?? {
    alert_name: '',
    alert_type: '',
    content_id: '',
  };
  const alertEntity = detection.alert_entity ?? {
    entity_id: '',
    entity_name: '',
    entity_type: '',
    entity_category: null,
  };
  const assignmentInfoRaw = detection.assignment_info ?? {
    assigned_at: 0,
    assigned_to_username: '',
    assigned_to_user_alias: '',
  };
  const detectionTimestampRaw = detection.detection_timestamp ?? {
    start: 0,
    end: 0,
  };
  const rankRaw = detection.rank ?? {
    severity: 0,
    is_custom_severity: false,
  };
  const updateInfoRaw = detection.update_info ?? {
    last_updated_by: '',
    last_updated_timestamp: 0,
  };
  const escalationDataRaw = detection.escalation_data ?? {
    escalation_status: false,
    escalated_by_user_alias: '',
    escalated_timestamp: 0,
    escalation_service_name: null,
  };
  const tenantInfoRaw = detection.tenant_info ?? {
    tenant_id: '',
    tenant_display_name: '',
  };
  return {
    ...detection,
    detection_keys: keys,
    alert_info: alertInfo,
    alert_entity: alertEntity,
    assignment_info: assignmentInfoRaw,
    detection_timestamp: detectionTimestampRaw,
    earliest_start_timestamp: detection.earliest_start_timestamp ?? 0,
    latest_start_timestamp: detection.latest_start_timestamp ?? 0,
    rank: rankRaw,
    update_info: updateInfoRaw,
    escalation_data: {
      ...escalationDataRaw,
      escalation_service_name:
        escalationDataRaw.escalation_service_name ?? null,
    },
    logscale_query: detection.logscale_query ?? '',
    tenant_info: tenantInfoRaw,
    paginated_destination_entities: sanitizePaginatedEntity(
      detection.paginated_destination_entities,
    ),
    paginated_source_entities: sanitizePaginatedEntity(
      detection.paginated_source_entities,
    ),
    detection_count: (detection as SantizedDetection).detection_count ?? 0,
    first_seen: (detection as SantizedDetection).first_seen ?? 0,
    severity: (detection as SantizedDetection).severity ?? 0,
    total_detections: (detection as SantizedDetection).total_detections ?? 0,
    // Flattened fields for SantizedDetection
    'rank.severity': rankRaw.severity ?? 0,
    'alert_info.alert_name': alertInfo.alert_name ?? '',
    'alert_info.alert_type': alertInfo.alert_type ?? '',
    'alert_entity.entity_name': alertEntity.entity_name ?? '',
    'rank.is_custom_severity': rankRaw.is_custom_severity ?? false,
    'tenant_info.tenant_display_name': tenantInfoRaw.tenant_display_name ?? '',
    'assignment_info.assigned_to_user_alias':
      assignmentInfoRaw.assigned_to_user_alias ?? '',
    'escalation_data.escalation_status':
      escalationDataRaw.escalation_status ?? false,
  } as SantizedDetection;
}

export const ActionsMenuButtonV2 = ({
  detection,
  refetchQueries,
  excludedEntitiesData,
  excludedEntitiesLoading,
  userRole,
  metadataLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
}: ActionsMenuProps) => {
  const toast = useToast();
  const { detectionData: contextDetectionData } = useDetectionContext();
  const { suppression, setSelectedEntity, selectedEntity } =
    useEntitiesContext();

  // Use the prop detection if provided (for row actions), otherwise fall back to context
  const detectionData = detection ?? contextDetectionData;

  const { isNotesDrawerLoading } = useContext(SlideDrawerContext);
  const { isOpen, onToggle, onClose: closePopover } = useDisclosure();
  const {
    isOpen: isAddNoteModalOpen,
    onOpen: addNoteModalOnOpen,
    onClose: addNoteModalOnClose,
  } = useDisclosure();
  const [popoverType, setPopoverType] = useState<null | string>(null);

  const [closeLoading, setCloseLoading] = useState<boolean>(false);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [sendToLoading, setSendToLoading] = useState<boolean>(false);

  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [isSentTo, setIsSentTo] = useState<boolean>(false);
  const {
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  } = useContext(SlideDrawerContext);
  const { hasCopied, onCopy } = useClipboard(window.location.href);

  const onClose = () => {
    setPopoverType(null);
    closePopover();
  };
  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'top',
        status: 'success',
        description: copiedUrl,
      });
    }
  }, [hasCopied, toast]);

  const {
    loading: escalationConfigurationsLoading,
    data: escalationConfigurationsData,
  } = useQueryEscalationConfigurationsPublic({});

  const serviceNowConfigurationRaw =
    escalationConfigurationsData?.queryEscalationConfigurations
      ?.escalation_configurations?.[0];
  const serviceNowConfiguration = serviceNowConfigurationRaw
    ? {
        ...serviceNowConfigurationRaw,
        service_name:
          serviceNowConfigurationRaw.service_name as ServiceNameEnum,
      }
    : undefined;

  const sanitizedDetectionData = sanitizeDetection(detectionData as Detection);

  const sourceEntity = useMemo(
    () => getSourceEntityForSuppression(sanitizedDetectionData),
    [sanitizedDetectionData],
  );

  // Use shared hook to determine suppression status
  const isSuppressed = useEntitySuppressionStatus({
    selectedEntity,
    sourceEntity,
    excludedEntitiesData,
  });

  useEffect(() => {
    setIsClosed(detectionData?.detection_status !== 'open');
    setIsSentTo(!!detectionData?.escalation_data?.escalation_status);
  }, [detectionData]);

  const menuDisabled =
    closeLoading ||
    suppression.pendingUpdate ||
    assignLoading ||
    sendToLoading ||
    isNotesDrawerLoading ||
    escalationConfigurationsLoading;

  return userRole === 'viewer' ? (
    // ACTION BUTTONS FOR VIEWER ROLE
    <HStack>
      <Button
        onClick={onCopy}
        variant="solid"
        colorScheme="gray"
        height="28px"
        data-testid="detection-details-header-copy-button-v2"
      >
        <ContentCopySharpIcon boxSize={5} />
        {copyDetectionUrl}
      </Button>
      {!metadataLoading && (
        <Tooltip label={notes} openDelay={500} placement="bottom-end">
          <Button
            size="md"
            variant="solid"
            colorScheme="gray"
            aria-label="notes"
            height="28px"
            px="2"
            py="1.5"
            onClick={() => {
              setPortalType?.('notes');
              setSlideDrawerTitle?.(
                <>
                  <Box>{notes}</Box>
                  {userRole !== 'viewer' && (
                    <Button
                      variant="solid"
                      colorScheme="gray"
                      marginTop="1"
                      onClick={addNoteModalOnOpen}
                    >
                      {addNote}
                    </Button>
                  )}
                </>,
              );
              setSlideDrawerBody?.(
                <DetectionNotesDrawer item={sanitizedDetectionData} />,
              );
              setSlideDrawerActions?.(null);
              setIsSlideDrawerOpen?.(true);
            }}
            p={3}
            data-testid="notes"
            leftIcon={<CommentSharpIcon boxSize={5} />}
          >
            {notes}
          </Button>
        </Tooltip>
      )}
    </HStack>
  ) : (
    // ACTION BUTTONS FOR ADMIN / OTHER NON-VIEWER ROLES
    <>
      <Tooltip
        offset={[-9, 6]}
        placement="bottom-end"
        label={isClosed ? detectionIsClosed : null}
        shouldWrapChildren={false}
      >
        <Button
          size="md"
          variant="solid"
          colorScheme="gray"
          isDisabled={isClosed}
          height="28px"
          px="2"
          py="1.5"
          data-testid="close-detection-trigger-actions-menu-button-v2"
          onClick={() => {
            onToggle();
            setPopoverType('close');
          }}
          leftIcon={<CheckCircleOutlinedIcon boxSize={5} />}
        >
          {close}
        </Button>
      </Tooltip>
      <Button
        size="md"
        variant="solid"
        colorScheme="gray"
        px="2"
        py="1.5"
        height="28px"
        data-testid="assign-detection-trigger-actions-menu-button-v2"
        onClick={() => {
          onToggle();
          setPopoverType('assign');
        }}
        leftIcon={<AccountCircleOutlinedIcon boxSize={5} />}
      >
        {assignTo}
      </Button>
      {!metadataLoading && (
        <Tooltip label={notes} openDelay={500} placement="bottom-end">
          <Button
            size="md"
            variant="solid"
            colorScheme="gray"
            aria-label="notes"
            height="28px"
            px="2"
            py="1.5"
            onClick={() => {
              setPortalType?.('notes');
              setSlideDrawerTitle?.(
                <>
                  <Box>{notes}</Box>
                  {userRole !== 'viewer' && (
                    <Button
                      variant="solid"
                      colorScheme="gray"
                      marginTop="1"
                      onClick={addNoteModalOnOpen}
                    >
                      {addNote}
                    </Button>
                  )}
                </>,
              );
              setSlideDrawerBody?.(
                <DetectionNotesDrawer item={sanitizedDetectionData} />,
              );
              setSlideDrawerActions?.(null);
              setIsSlideDrawerOpen?.(true);
            }}
            p={3}
            data-testid="notes"
            leftIcon={<CommentSharpIcon boxSize={5} />}
          >
            {notes}
          </Button>
        </Tooltip>
      )}
      <VStack>
        <Popover
          isOpen={isOpen}
          offset={[0, 10]}
          onClose={onClose}
          placement="bottom-end"
        >
          <Menu placement="bottom-end">
            <PopoverTrigger>
              <MenuButton
                as={Button}
                variant="solid"
                disabled={menuDisabled}
                colorScheme="gray"
                size="md"
                paddingLeft={0}
                paddingRight={0}
                width="28px"
                height="28px"
                data-testid="detection-trigger-actions-menu-button-v2"
              >
                <Box
                  marginTop={1}
                  marginBottom={1}
                  transform="rotate(90deg)"
                  transformOrigin="center"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <MoreVertSharpIcon boxSize={5} />
                </Box>
              </MenuButton>
            </PopoverTrigger>
            <MenuList zIndex="popover">
              <VStack spacing={0} alignItems="start">
                <MenuItem
                  onClick={onCopy}
                  icon={<ContentCopySharpIcon boxSize={5} />}
                >
                  {copyDetectionUrl}
                </MenuItem>
                <Tooltip
                  offset={[-9, 6]}
                  placement="bottom-end"
                  shouldWrapChildren={true}
                  label={
                    isSentTo
                      ? detectionIsSent
                      : !sendToLoading &&
                          !escalationConfigurationsLoading &&
                          !serviceNowConfiguration?.enabled
                        ? SNConfigureAndEnable
                        : null
                  }
                >
                  <MenuItem
                    isDisabled={
                      isSentTo ||
                      !serviceNowConfiguration ||
                      !serviceNowConfiguration?.enabled
                    }
                    data-testid="send-to-trigger-actions-menu-button-v2"
                    onClick={() => {
                      onToggle();
                      setPopoverType('sendTo');
                    }}
                    icon={<ForwardSharpIcon boxSize={5} />}
                  >
                    {sendTo}
                  </MenuItem>
                </Tooltip>
                <SuppressEntityMenuItem
                  isSuppressed={isSuppressed}
                  isDisabled={!sourceEntity}
                  onClick={() => {
                    // Map detection data to EntityInstance and set in context
                    if (sourceEntity && detectionData) {
                      const entityInstance = {
                        ...getEntityInstanceFromDetection(
                          detectionData,
                          sourceEntity,
                        ),
                        entityIsSuppressed: isSuppressed,
                      };
                      setSelectedEntity(entityInstance);
                    }
                    onToggle();
                    setPopoverType('suppress');
                  }}
                  testId="suppress-entity-trigger-actions-menu-button-v2"
                  useOutlinedIcons={true}
                  suppressText={suppress}
                  unsuppressText={unSuppress}
                />
              </VStack>
            </MenuList>
          </Menu>
          {popoverType === 'assign' && (
            <AssignUserContent
              onClose={onClose}
              setIsLoading={setAssignLoading}
              isLoading={assignLoading}
              refetchMain={true}
              getDetectionVariables={getDetectionVariables}
              queryDetectionsPaginatedVariables={
                queryDetectionsPaginatedVariables
              }
            />
          )}
          {popoverType === 'close' && (
            <CloseDetectionContent
              onClose={onClose}
              setIsClosed={setIsClosed}
              setIsLoading={setCloseLoading}
              refetchQueries={refetchQueries}
              refetchMain={true}
              excludedEntitiesLoading={excludedEntitiesLoading}
              isLoading={closeLoading}
            />
          )}
          {popoverType === 'sendTo' && (
            <SendToContent
              onClose={onClose}
              setIsSentTo={setIsSentTo}
              setIsLoading={setSendToLoading}
              refetchQueries={refetchQueries}
              refetchMain={true}
              serviceNowConfiguration={serviceNowConfiguration}
              excludedEntitiesLoading={excludedEntitiesLoading}
              isLoading={sendToLoading}
            />
          )}
          {popoverType === 'suppress' && (
            <SuppressEntityContent
              onClose={onClose}
              refetchMain={true}
              excludedEntitiesLoading={excludedEntitiesLoading}
              isSuppressed={isSuppressed}
            />
          )}
        </Popover>
      </VStack>
      <Modal
        size="sm"
        isOpen={isAddNoteModalOpen}
        onClose={addNoteModalOnClose}
        title={addNote}
        body={
          <AddNoteContent onClose={addNoteModalOnClose} refetchMain={true} />
        }
      />
    </>
  );
};
